/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.̦
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef } from 'preact/hooks';
import { useVisEvent } from '../hooks/PRIVATE_useVisEvents/useVisEvents';
import { getSectionalNavUtils } from './sectionalLegendNavUtils';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { getRandomId } from '../utils/PRIVATE_visLayoutUtils/layoutUtils';
import {
  getDatatipText,
  getAriaProps,
  getGridNums,
  getLegendTextStyle,
  getIsLegendItemActionable,
  isEqualItem,
  getItemInfo,
  getKeyUpHandler
} from '../utils/PRIVATE_legendUtils/legendUtils';
import { useLegendDatatip } from '../UNSAFE_Legend/useLegendDatatip';
import { LegendText } from '../UNSAFE_Legend/LegendText';
import { useItemFocus } from '../hooks/PRIVATE_useVisEvents';
import { useLegendLayout } from '../UNSAFE_Legend/useLegendLayout';
import { Flex } from '../UNSAFE_Flex';
import { styles } from '../UNSAFE_Legend/themes/LegendStyles.css';
import { useCssVars } from '../hooks/PRIVATE_useCssVars';
import { legendVars } from '../utils/PRIVATE_legendUtils/legendUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { useTestId } from '../hooks/UNSAFE_useTestId';
import { BaseLegend } from '../PRIVATE_BaseLegend//BaseLegend';
import { useLegendContextMenu } from '../PRIVATE_BaseLegend/useLegendContextMenu';
import { SectionalLegendProps } from './sectionalLegend.type';
import {
  ItemFocusDetail,
  ItemActionDetail,
  ItemInfo,
  LegendItemData,
  LegendSectionData
} from '../utils/UNSAFE_visTypes/legend';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { merge } from '#utils/UNSAFE_stringUtils';

/**
 * A Sectional Legend allows grouping of legend items in sections with a specific title.
 */
export function SectionalLegend<
  K extends string | number,
  D extends LegendItemData<K>,
  S extends LegendSectionData<K, D>
>({
  orientation = 'horizontal',
  sectionTitleHAlign = 'start',
  sections,
  isReadOnly = true,
  onItemAction,
  onItemHover,
  onItemFocus,
  testId,
  contextMenuConfig,
  hideAndShowBehavior = 'off',
  hoverBehavior = 'none',
  ...props
}: SectionalLegendProps<K, D, S>) {
  const testIdProps = useTestId(testId);
  const rootRef = useRef<HTMLDivElement>(null);
  const rootDimsRef = useRef<DOMRect>();
  const { resolvedVars, cssContent } = useCssVars(legendVars);

  const { getItem, getDetailFromInfo, getPrevItemInfo, getNextItemInfo } = getSectionalNavUtils<
    K,
    D,
    S
  >(sections);
  const legendFontStylesObj = getLegendTextStyle(
    {
      fontStyle: props.textFontStyle,
      fontSize: props.textFontSize?.toString(),
      fontWeight: props.textFontWeight?.toString(),
      fontFamily: props.textFontFamily
    },
    resolvedVars
  );

  const isLegendItemActionable = getIsLegendItemActionable(sections, isReadOnly, true);

  const { textMeasureContent, layoutInfo } = useLegendLayout({
    rootRef,
    rootDimsRef,
    resolvedVars,
    sections,
    orientation,
    legendFontStylesObj,
    symbolWidth: props.symbolWidth
  });

  const { touchResponse, touchResponseStyle } = useVisTouchResponse({ type: 'touchStart' });

  const { focusedItemInfo, hoveredItemInfo, onContextMenuDismissed, activeId, eventsProps } =
    useVisEvent<ItemInfo, ItemActionDetail<K, D>, ItemFocusDetail<K, D>, D>(
      touchResponse,
      !isReadOnly,
      isLegendItemActionable,
      getKeyUpHandler,
      getDetailFromInfo,
      getPrevItemInfo,
      getNextItemInfo,
      onItemAction,
      onItemHover,
      onItemFocus,
      isEqualItem,
      getItemInfo,
      { itemIdx: 0, sectionIdx: 0 }
    );

  const { focusedItemRef } = useItemFocus(focusedItemInfo);
  const text = getDatatipText(sections, getItem, focusedItemInfo, hoveredItemInfo);
  const {
    datatipContent,
    datatipProps: { 'aria-describedby': datatipAriaDescribedby, ...datatipProps }
  } = useLegendDatatip({
    text,
    rootDimsRef,
    focusedItemInfo,
    focusedItemRef,
    touchResponse
  });
  const ariaProps = getAriaProps<K>(
    isReadOnly,
    props['aria-label'],
    props.hiddenIds,
    merge([props['aria-describedby'], datatipAriaDescribedby]),
    props['aria-labelledby']
  );

  const { contextMenuContent, contextMenuProps } = useLegendContextMenu<K, D, S>({
    rootRef,
    contextMenuConfig,
    focusedItemInfo,
    sections,
    isReadOnly,
    onContextMenuDismissed
  });

  const mergedProps = mergeProps(eventsProps, datatipProps, contextMenuProps);
  const titleStyle = {
    fontFamily: props.sectionTitleFontFamily,
    fontSize: props.sectionTitleFontSize,
    color: props.sectionTitleColor,
    fontStyle: props.sectionTitleFontStyle,
    fontWeight: props.sectionTitleFontWeight,
    textDecoration: props.sectionTitleTextDecoration
  };
  const { sectionalLegendBaseStyles, renderedLegendStyle, preRenderedLegendStyle } = styles;
  const isHoriz = orientation === 'horizontal';
  const canRender = orientation === 'vertical' || !!layoutInfo;
  return (
    <div
      ref={rootRef}
      tabIndex={isReadOnly ? undefined : 0}
      role="application"
      class={classNames([
        sectionalLegendBaseStyles,
        canRender ? renderedLegendStyle : preRenderedLegendStyle
      ])}
      style={touchResponseStyle}
      {...testIdProps}
      {...ariaProps}
      {...mergedProps}
      aria-activedescendant={activeId}>
      {canRender ? (
        <Flex direction={isHoriz ? 'row' : 'column'} wrap={'wrap'} gap={'4x'}>
          {sections.map((section: S, index: number) => {
            const labelledById = !isReadOnly ? getRandomId() : '';
            const isWrappedSection = layoutInfo ? layoutInfo.isWrapped : false;
            const flexDirection = !isHoriz || isWrappedSection ? 'column' : 'row';
            const alignItems = !isHoriz || isWrappedSection ? 'start' : 'center';
            const { itemsPerCol, itemsPerRow } = getGridNums(section.items.length, layoutInfo);
            return (
              <Flex
                align={alignItems}
                direction={flexDirection}
                maxWidth={'100%'}
                justify={'start'}
                wrap="wrap">
                <LegendText
                  text={section.title}
                  {...titleStyle}
                  type="title"
                  id={labelledById}
                  align={sectionTitleHAlign}
                />
                <BaseLegend
                  {...props}
                  focusedItemRef={focusedItemRef}
                  labelledBy={labelledById}
                  sectionIdx={index}
                  items={section.items}
                  orientation={orientation}
                  itemsPerRow={itemsPerRow}
                  itemsPerCol={itemsPerCol}
                  itemWidth={layoutInfo?.isWrapped ? layoutInfo?.maxItemWidth : undefined}
                  isReadOnly={isReadOnly}
                  focusedItemInfo={focusedItemInfo}
                  hoveredItemInfo={hoveredItemInfo}
                  activeId={activeId}
                  hideAndShowBehavior={hideAndShowBehavior}
                  hoverBehavior={hoverBehavior}
                />
              </Flex>
            );
          })}
        </Flex>
      ) : null}
      {datatipContent}
      {textMeasureContent}
      {cssContent}
      {contextMenuContent}
    </div>
  );
}
